import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { verifyToken } from '@adornis/users/server/token.js';
import { getUserFromContext } from '@adornis/users/server/util.js';
import { DateTime } from 'luxon';
import type { Filter, InsertOneResult } from 'mongodb';
import { catchError, from, of, switchMap } from 'rxjs';
import { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { Partner } from '../master-data-management/partner/entities/Partner.js';
import { CRVertrag } from '../master-data-management/users/entities/CRVertrag.js';
import { Mitarbeiter } from '../master-data-management/users/entities/Mitarbeiter.js';
import { rebuildPermissionsForUsers } from '../master-data-management/users/rebuildPermissionsForUsers.js';
import { ContextPrefix } from '../permissions.js';
import { ChancenRole } from './ChancenRole.js';
import { SemanticRelationship } from './SemanticRelationship.js';

/**
 * @entity Taetigkeit
 */
@Entity()
export class Taetigkeit extends SemanticRelationship {
  public static override _class = 'Taetigkeit';

  @DataLoadedField(() => Mitarbeiter, ({ subjectID }) => subjectID)
  public override subject: Maybe<Mitarbeiter>;

  @DataLoadedField(() => KPF, ({ objectID }) => objectID)
  public override object: Maybe<KPF>;

  @Field(() => String)
  public einsaetzeProWoche: Maybe<string>;

  @Field(() => Float)
  public aufwandsentschaedigung: Maybe<number>;

  @Field(() => Float)
  public aufwandsentschaedigungTeamtreffen: Maybe<number>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public startDate: Maybe<DateTime>;

  @Field(() => DateTime)
  public endDate: Maybe<DateTime>;

  @Field(() => DateTime)
  public preliminaryEndDate: Maybe<DateTime>;

  //* * This field is only important upon creation. It specifies in which vertrag of the user this taetigkeit should be inserted. Default to the last contract of the user.
  //* * Takes an index. -1 is the last place of the array
  @Field(() => Int, { default: v => v ?? -1 })
  public insertIntoIndex: Maybe<number>;

  public override async isDuplicate(checkSelf: boolean = true) {
    const relationshipCollection = await getCollection(this._class);
    const duplicateRelationships = await relationshipCollection
      .find({ predicateID: this.predicateID, subjectID: this.subjectID, objectID: this.objectID })
      .toArray();

    if (!duplicateRelationships.length) return false;

    for (const duplicateRelationship of duplicateRelationships) {
      if (!checkSelf && duplicateRelationship._id === this._id) continue;
      const constructedDuplicateRelationship = constructValue(duplicateRelationship) as Taetigkeit;

      if (this.isActive() && constructedDuplicateRelationship.isActive()) return true;
    }

    return false;
  }

  public override getContext() {
    return this.objectID === GLOBAL_CONTEXT ? GLOBAL_CONTEXT : ContextPrefix.KPF + this.objectID;
  }

  public override isActive(): boolean {
    return (
      !!this.startDate &&
      this.startDate.startOf('day') <= DateTime.now().startOf('day') &&
      (!this.endDate || this.endDate.endOf('day') > DateTime.now().startOf('day'))
    );
  }

  public static override isActiveMongoSelector(time: DateTime = DateTime.now()): Filter<EntityData<Taetigkeit>> {
    return {
      $or: [
        { endDate: null },
        { endDate: undefined },

        // * endDate in the DB is the start of the day, but we want to keep active taetigkeiten until the end of the day
        // * so we need to subtract one day from the current time and set it to the end of the day.
        { endDate: { $gt: time.minus({ day: 1 }).endOf('day').toJSDate() } },
      ],
    };
  }

  @Subscription(() => [Taetigkeit])
  static subscribeActiveTaetigkeitenForKPFs(@Arg('kpfIDs', () => [String]) kpfIDs: string[]) {
    return (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      const selector = { _class: this._class, objectID: { $in: kpfIDs }, ...this.isActiveMongoSelector() };
      const handle = getCollectionHandle<Taetigkeit>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as Taetigkeit[]);
        }),
      );
    };
  }

  @Subscription(() => [Taetigkeit])
  static subscribeTaetigkeitForKPF(@Arg('kpfID', () => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      const selector = { _class: this._class, objectID: kpfID };
      const handle = getCollectionHandle<Taetigkeit>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as Taetigkeit[]);
        }),
      );
    };
  }

  @Subscription(() => [Taetigkeit])
  static subscribeTaetigkeitForUser(@Arg('userID', () => String) userID: string) {
    return (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      const selector = { _class: this._class, subjectID: userID };
      const handle = getCollectionHandle<Taetigkeit>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as Taetigkeit[]);
        }),
      );
    };
  }

  /**
   * Returns the Tätigkeiten of a given subject (Mitarbeiter) with object (KPF+School) and predicate
   * (ChancenRole) manually resolved, bypassing read permissions. Used by both the session-based
   * `getOwnTaetigkeiten` and the token-based `getTaetigkeitenWithOneTimeToken`.
   */
  private static async _resolveTaetigkeitenForSubject(subjectID: string): Promise<Taetigkeit[]> {
    const coll = await getRawCollection<EntityData<Taetigkeit>>(SemanticRelationship._collectionName);
    const rawResults = await coll.find({ _class: Taetigkeit._class, subjectID }).toArray();

    const kpfColl = await getRawCollection<any>(KPF._collectionName);
    const partnerColl = await getRawCollection<any>(Partner._collectionName);
    const roleColl = await getRawCollection<any>(ChancenRole._collectionName);

    const taetigkeiten: Taetigkeit[] = [];
    for (const raw of rawResults) {
      const t = constructValue(raw) as Taetigkeit;

      if (raw.objectID) {
        const kpfDoc = await kpfColl.findOne({ _id: raw.objectID });
        if (kpfDoc) {
          const kpf = constructValue(kpfDoc) as KPF;
          if (kpfDoc.schoolID) {
            const schoolDoc = await partnerColl.findOne({ _id: kpfDoc.schoolID });
            if (schoolDoc) (kpf as any).school = constructValue(schoolDoc);
          }
          t.object = kpf;
        }
      }

      if (raw.predicateID) {
        const roleDoc = await roleColl.findOne({ _id: raw.predicateID });
        if (roleDoc) t.predicate = constructValue(roleDoc) as any;
      }

      taetigkeiten.push(t);
    }

    return taetigkeiten;
  }

  /**
   * Returns the current user's own Tätigkeiten, bypassing publishFilter.
   * Used during onboarding where ExternerMitarbeiter has no read permission on Tätigkeiten.
   */
  @Query(() => [Taetigkeit])
  public static getOwnTaetigkeiten() {
    return async (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');
      return Taetigkeit._resolveTaetigkeitenForSubject(caller._id);
    };
  }

  /**
   * Token-authenticated variant used by the onboarding PDF print components (which run in a detached
   * puppeteer browser without a session). Mirrors `Mitarbeiter.getMitarbeiterWithOneTimeToken` — caller
   * generates a short-lived signed token, passes `{ mitarbeiterID, token }` as query params.
   */
  @Query(() => [Taetigkeit])
  public static getTaetigkeitenWithOneTimeToken(
    @Arg('mitarbeiterID', () => ID) mitarbeiterID: string,
    @Arg('token', () => String) token: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Taetigkeit>) => {
      await verifyToken(token);
      return Taetigkeit._resolveTaetigkeitenForSubject(mitarbeiterID);
    };
  }

  /**
   * @description It saves the entity with all business logic, but **doesn't** update the roles on the user
   */
  override save(): Promise<number> {
    return super.save();
  }

  // Dont check if a field is missing. In this case the transformBeforeUpdate method repairs this error. Dont call super here
  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: SemanticRelationship,
  ): Promise<void> {
    if (await update.isDuplicate(false)) {
      throw new Error('Diese Beziehung gibt es bereits!');
    }
  }

  /**
   * Tätigkeiten have a few special cases. If the relationship is altered we dont want to simply change it, because we need to have old relationships archived. The special behaviors are as followed:
   * - If any connection is deleted we want to keep the old entity and save an endDate
   * - If any connection is changed to another entity, create a new one for this change and keep the old
   * @param oldEntity
   * @param update
   */
  static override async transformBeforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: Taetigkeit,
  ): Promise<void> {
    const oldTaetigkeit = await Taetigkeit.getByID<Taetigkeit>(oldEntity._id ?? '')({
      _class: 1,
      _id: 1,
      subjectID: 1,
      predicateID: 1,
      objectID: 1,
      endDate: 1,
    });

    //* * Handle the deletion of connections. We instead want to keep the relationship and set the endDate
    if (oldTaetigkeit?.subjectID && update.subjectID === null) {
      update.subjectID = oldTaetigkeit.subjectID;
      update.endDate = DateTime.now();
    }

    if (oldTaetigkeit?.predicateID && update.predicateID === null) {
      update.objectID = oldTaetigkeit.predicateID;
      update.endDate = DateTime.now();
    }

    if (oldTaetigkeit?.objectID && update.objectID === null) {
      update.objectID = oldTaetigkeit.objectID;
      update.endDate = DateTime.now();
    }

    //* * Check whether the update is a valid relationship
    if (!update.isValid()) {
      throw new Error('Diese Beziehung ist nicht korrekt!');
    }

    //* * If a connection changes we want to keep it an make a new relationship for the update
    if (
      (oldTaetigkeit?.subjectID && oldTaetigkeit.subjectID !== update.subjectID) ||
      (oldTaetigkeit?.predicateID && oldTaetigkeit.predicateID !== update.predicateID) ||
      (oldTaetigkeit?.objectID && oldTaetigkeit.objectID !== update.objectID)
    ) {
      // Create a new entity for the update
      const newTaetigkeit = new Taetigkeit({
        subjectID: update.subjectID,
        objectID: update.objectID,
        predicateID: update.predicateID,
        startDate: DateTime.now(),
      });

      // Reset updated entity and set endDate
      update.subjectID = oldTaetigkeit.subjectID;
      update.predicateID = oldTaetigkeit.predicateID;
      update.objectID = oldTaetigkeit.objectID;
      update.endDate = DateTime.now();

      await newTaetigkeit.create();
    }

    await super.transformBeforeUpdate(oldEntity, update);
  }

  /**
   * After create handles the update of the verträge on the user. All Taetigkeiten are inserted into a verträg to keep track of them.
   * The Taetigkeit insertion defaults to the last (most recent) vertrag on the user.
   * @param entity The Taetigkeit which got created
   * @param result
   */
  static override async afterCreate(entity: Taetigkeit, result: InsertOneResult<Document>) {
    await super.afterCreate(entity, result);

    // get the user from the database, based on the subject of the relationship
    // Use getRawCollection to bypass publishFilter. A newly created employee is not yet
    // connected to any KPF context and would be invisible to getByID permission checks.
    const userCol = await getRawCollection<EntityData<Mitarbeiter>>(Mitarbeiter._collectionName);
    const rawUser = await userCol.findOne({ _id: entity.subjectID });
    if (!rawUser) throw new Error('Couldnt find user for subject with _id ' + entity.subjectID);
    const user = constructValue(rawUser) as Mitarbeiter;

    // The user might be new. If so insert an empty contract
    if (!user.vertraege.length) {
      user.vertraege = [new CRVertrag({})];
    }

    // -1 means it should insert into the last place (default)
    if (entity.insertIntoIndex === -1) entity.insertIntoIndex = user.vertraege.length - 1;

    // check for out of bounds indices
    if (
      entity.insertIntoIndex === undefined ||
      entity.insertIntoIndex === null ||
      entity.insertIntoIndex > user.vertraege.length ||
      entity.insertIntoIndex < 0
    ) {
      throw new Error(
        'Tried to insert Taetigkeit into vertrag with index: ' +
          entity.insertIntoIndex +
          '. But array has length: ' +
          user.vertraege.length,
      );
    }

    // push id into vertrag and save the user
    user.vertraege[entity.insertIntoIndex]?.taetigkeitenIDs?.push(entity._id);
    await user.save();

    // After the user is saved we can regenerate his permissions
    await rebuildPermissionsForUsers([user._id]);
  }

  static override async beforeRemove(id: string, entityData: Taetigkeit): Promise<void> {
    const taetigkeit = await Taetigkeit.getByID<Taetigkeit>(id)(Taetigkeit.allFields);
    if (!taetigkeit?.createdAt) throw new Error("Can't remove Taetigkeit with no creation Date");
    if (taetigkeit.createdAt.startOf('day') < DateTime.now().startOf('day').minus({ days: 2 }))
      throw new Error('Die Tätigkeit ist älter als 2 Tage und kann nicht mehr gelöscht werden!');

    return super.beforeRemove(id, entityData);
  }

  static override async afterRemove(id: string, entityData: Taetigkeit): Promise<void> {
    const user = await Mitarbeiter.getByID<Mitarbeiter>(entityData.subjectID ?? '')({
      _class: 1,
      _id: 1,
      roles: AdornisRoleToContextsWrapper.allFields,
      vertraege: { _class: 1, _id: 1, taetigkeitenIDs: 1 },
    });
    if (!user) return super.afterRemove(id, entityData);
    for (const vertrag of user.vertraege) {
      const indexOfTaetigkeit = vertrag.taetigkeitenIDs?.indexOf(id);
      if (indexOfTaetigkeit && indexOfTaetigkeit !== -1) {
        vertrag.taetigkeitenIDs?.splice(indexOfTaetigkeit, 1);
      }
    }
    await user.save();

    return super.afterRemove(id, entityData);
  }
}
